import { auditExportTable, type AuditExportSelection } from './auditModel'

/**
 * PD-1322 (S11) — CSV serialization of an audit export, following the
 * assistant-table pattern (`pages/Assistant/components/assistant-table-csv.ts`):
 * plain string building, RFC-4180 quoting, no dependencies. Export is ungated per
 * Jira comment 24733 (providers and care coordinators can view and export).
 *
 * WHAT this file writes is decided elsewhere: `selectAuditExportRows` picks the rows and
 * `auditExportTable` projects the cells, both shared with the PDF export (audit-pdf.ts).
 * This module only turns that table into CSV text.
 */

const formatCsvCell = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return ''
  const text = String(value)
  return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

const toCsv = (columns: string[], rows: string[][]): string =>
  [
    columns.map(formatCsvCell).join(','),
    ...rows.map((row) => row.map(formatCsvCell).join(',')),
  ].join('\n')

/**
 * A CSV leaves the building as a standalone artefact, so an incomplete selection must say
 * so INSIDE the file — the on-screen banner doesn't travel with it (the PDF's INCOMPLETE
 * RECORD block is this same statement in its format). Leading lines, `#` prefixed so
 * anything parsing rows can skip them, before the header.
 *
 * TWO notices, because there are two unrelated reasons the rows can be short and they call
 * for different actions from the reader: a sync that has not finished will fill in on its
 * own, a capped page never will. Both can be true of one export.
 */
const SYNC_PENDING_NOTICE =
  "# INCOMPLETE RECORD: this chart's change history was still being derived when this export ran. Changes older than the rows below may not have been read yet. Re-export once the tab stops reporting it."
const CAPPED_NOTICE =
  '# INCOMPLETE RECORD: this export reached the server page limit; older matching events are not included. Narrow the filters or page further back, and export again, to retrieve them.'

export function buildAuditCsv(selection: AuditExportSelection): string {
  const { columns, rows } = auditExportTable(selection)
  // Sync first: it explains why rows are missing from the OLD end of a list that is still
  // being built, which is the reader's first question when a count looks wrong.
  const notices = [
    ...(selection.syncPending ? [SYNC_PENDING_NOTICE] : []),
    ...(selection.capped ? [CAPPED_NOTICE] : []),
  ]
  return [...notices, toCsv(columns, rows)].join('\n')
}

/** Browser download via a transient object URL (same mechanics as AssistantTable). */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
